import { useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [ticketIds, setTicketIds] = useState([]);
  const [isGuestPurchase, setIsGuestPurchase] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      const reference = searchParams.get('reference');
      const trxref = searchParams.get('trxref');
      const ref = reference || trxref;

      if (!ref) {
        setStatus('failed');
        return;
      }

      try {
        // First try guest verification (doesn't require auth)
        try {
          const guestResponse = await axios.post(
            `${API_URL}/api/tickets/guest/payment/verify`,
            null,
            { params: { reference: ref } }
          );
          
          if (guestResponse.data.status === 'completed') {
            setStatus('success');
            setIsGuestPurchase(true);
            setGuestEmail(guestResponse.data.guest_email || '');
            setTicketIds(guestResponse.data.tickets || []);
            toast.success('Payment successful! Check your email for tickets.');
            return;
          }
        } catch (guestError) {
          // Expected: 404/400 means it's not a guest transaction — fall through to regular verification
          if (guestError.response?.status >= 500) {
            console.error('Guest payment verification server error:', guestError.response?.status);
          }
        }

        // Try public verification endpoint for authenticated users
        const response = await axios.post(
          `${API_URL}/api/tickets/payment/verify-public`,
          { reference: ref }
        );

        if (response.data.status === 'success') {
          setStatus('success');
          setTicketIds(response.data.tickets || []);
          toast.success('Payment successful!');
        } else {
          setStatus('failed');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Payment verification failed');
        setStatus('failed');
      }
    };

    verifyPayment();
  }, [searchParams]);

  return (
    <Layout showFooter={false}>
      <div className="min-h-[calc(100vh-10rem)] flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            {status === 'verifying' && (
              <>
                <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
                <h2 className="text-2xl font-bold mb-2">Verifying Payment</h2>
                <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
                <p className="text-muted-foreground mb-6">
                  Your tickets have been booked successfully. 
                  {isGuestPurchase 
                    ? ` Check your email (${guestEmail}) for your QR code tickets.` 
                    : ' Check your email for confirmation.'}
                </p>
                <div className="space-y-3">
                  {isGuestPurchase ? (
                    <>
                      <Link to={`/my-tickets/guest?email=${encodeURIComponent(guestEmail)}`}>
                        <Button className="w-full btn-primary" data-testid="view-tickets-btn">
                          View My Tickets
                        </Button>
                      </Link>
                      <Link to="/events">
                        <Button variant="outline" className="w-full" data-testid="browse-more-btn">
                          Browse More Events
                        </Button>
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to="/my-tickets">
                        <Button className="w-full btn-primary" data-testid="view-tickets-btn">
                          View My Tickets
                        </Button>
                      </Link>
                      <Link to="/events">
                        <Button variant="outline" className="w-full" data-testid="browse-more-btn">
                          Browse More Events
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Payment Failed</h2>
                <p className="text-muted-foreground mb-6">
                  We couldn't verify your payment. Please try again or contact support if the issue persists.
                </p>
                <div className="space-y-3">
                  <Link to="/events">
                    <Button className="w-full btn-primary" data-testid="try-again-btn">
                      Try Again
                    </Button>
                  </Link>
                  <Link to="/dashboard">
                    <Button variant="outline" className="w-full" data-testid="go-dashboard-btn">
                      Go to Dashboard
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
